import PERMISSIONS_DESCRIPTIONS from '~/server_shared/generated-types/PERMISSIONS_DESCRIPTIONS';

import { CONFIGURATION_FEATURES_NAMES } from '../../../Types';
import { isFeatureEnabled, reportErrorInProductionOrThrow } from '../../../Utils';

import { __DEFAULT_PROFILES__ } from './enums/DefaultProfiles';
import { __PERMISSION_ACTIONS__ } from './enums/PermissionActions';
import { __PERMISSION_VERBS__ } from './enums/PermissionVerbs';

const getImmutableObjectHandlerConfig = (objectType) => ({
  get: (target, name) => {
    if (name in target) {
      return name;
    }
    reportErrorInProductionOrThrow(`Permission ${objectType} ${name} is not defined`);
  },
  set: () => {
    reportErrorInProductionOrThrow(`Permission ${objectType}s are immutable`);
    return false;
  },
});

export const PERMISSION_ACTIONS = new Proxy(__PERMISSION_ACTIONS__, getImmutableObjectHandlerConfig('action'));

export const PERMISSION_VERBS = new Proxy(__PERMISSION_VERBS__, getImmutableObjectHandlerConfig('verb'));

/**
 * @type {PERMISSION_ACTIONS}
 */
export const PERMISSION_ACTIONS_CONFIG = __PERMISSION_ACTIONS__;
/**
 * @type {PERMISSION_VERBS}
 */
export const PERMISSION_VERBS_CONFIG = __PERMISSION_VERBS__;

// To avoid circular dependency we assign the permissions after the actions config and the default profiles are defined
Object.values(__DEFAULT_PROFILES__).forEach((profile) => profile.assignPermissions(PERMISSION_ACTIONS_CONFIG));
/**
 * @type {DEFAULT_PROFILES}
 */
export const DEFAULT_PROFILES_CONFIG = __DEFAULT_PROFILES__;

export const isVerbValid = (verb) => Object.keys(PERMISSION_VERBS).includes(verb);
export const isActionValid = (action) => Object.keys(PERMISSION_ACTIONS).includes(action);

export const getPermissionIdString = ({ action, verb }) =>
  action && verb ? `${action}:${verb}` : 'DEFAULT:WRITE or higher';

export const getPermissionDeniedTooltipText = ({ action, verb, actions }) => {
  let permissionIdString = getPermissionIdString({ action, verb });
  if (actions) {
    permissionIdString = actions.map(getPermissionIdString).join(', ');
  }

  return `Sorry, you don't have permission to access this feature.
Please contact your system administrator (Permission id: ${permissionIdString})`;
};

export const isPermissionsEnabled = (organization) => {
  return Boolean(organization && isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.PERMISSIONS_ENFORCEMENT));
};

const SYSCONFIG_PERMISSION_ACTION_IDS = new Set(
  Object.keys(__PERMISSION_ACTIONS__).filter((actionId) => actionId.startsWith('SYSCONFIG_'))
);

export const isSysconfigPermissionAction = (action) => SYSCONFIG_PERMISSION_ACTION_IDS.has(action);

const isGranularSysconfigEnforcementEnabled = (userOrganization) => isPermissionsEnabled(userOrganization);

const profileHasAction = (profile, action) => {
  return action in profile.permissions;
};

/**
 * Get the default verb for an action from PERMISSIONS_DESCRIPTIONS, if specified.
 * Actions can define 'default_verb' to override profile's global defaults.
 * @param {string} action - The permission action name
 * @returns {string|null} - The default verb or null if not specified
 */
const getActionDefaultVerb = (action) => {
  const desc = PERMISSIONS_DESCRIPTIONS[action];
  if (desc && desc.default_verb) {
    return desc.default_verb;
  }
  return null;
};

const profileHasVerbForAction = (profile, action, verbs) => {
  if (!profileHasAction(profile, action)) {
    return false;
  }
  const actionVerbs = profile.permissions[action];

  return actionVerbs.includes(PERMISSION_VERBS.FULL) || actionVerbs.filter((value) => verbs.includes(value)).length > 0;
};

export const isActionAllowed = ({ action, verb, profile, userOrganization, isSupervisor = false }) => {
  if (isSysconfigPermissionAction(action) && !isGranularSysconfigEnforcementEnabled(userOrganization)) {
    action = PERMISSION_ACTIONS.SYSTEM_CONFIGURATION;
  }

  if (!isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.PERMISSIONS_ENFORCEMENT)) {
    const defaultVerb = getActionDefaultVerb(action);
    if (defaultVerb === null) {
      return true;
    }
    // defaultVerb is the access level granted to users without explicit permission.
    // If it's FULL, or matches the requested verb, allow the action.
    if (defaultVerb === PERMISSION_VERBS.FULL || defaultVerb === verb) {
      return true;
    }
    return isSupervisor;
  }

  if (!profile) {
    return false;
  }

  if (profileHasVerbForAction(profile, action, [verb, PERMISSION_VERBS.FULL])) {
    return true;
  }

  if (profileHasVerbForAction(profile, action, PERMISSION_VERBS.FORBIDDEN)) {
    return false;
  }

  if (!profileHasAction(profile, action)) {
    // Check per-action default from PERMISSIONS_DESCRIPTIONS first
    const defaultVerb = getActionDefaultVerb(action);
    if (defaultVerb) {
      return defaultVerb === PERMISSION_VERBS.FULL || defaultVerb === verb;
    }
    // Fall back to profile's global defaults
    return profile.defaults.includes(PERMISSION_VERBS.FULL) || profile.defaults.includes(verb);
  }

  return false;
};

/**
 * isActionAllowed over a raw CMS context, so hook and non-hook callers share one
 * definition of how `user`/`userProfile`/`userOrganization` map onto its arguments.
 * @param {object} params
 * @param {object=} params.user
 * @param {object=} params.userProfile
 * @param {object=} params.userOrganization
 * @param {string} params.action
 * @param {string} params.verb
 * @returns {boolean}
 */
export const hasPermission = ({ user, userProfile, userOrganization, action, verb }) =>
  isActionAllowed({
    action,
    verb,
    profile: userProfile,
    userOrganization,
    isSupervisor: user?.is_org_level_supervisor ?? false,
  });
